import axios from 'axios';
import type {
  UploadPreflightResponse,
  UploadPreflightTarget,
} from '@/shared/apis/asset-upload/post-upload-preflight';
import type {
  UploadFailureClassification,
  UploadFailureReason,
} from './upload-error-classifier';
import { I18N_KEY_BY_REASON } from './upload-error-classifier';
import {
  getUploadPreflightDecision,
  type UploadPreflightDecision,
  UPLOAD_PREFLIGHT_MIN_BYTES,
} from './upload-preflight-policy';

const PREFLIGHT_TIMEOUT_MS = 15_000;
const PREFLIGHT_MAX_ATTEMPTS = 2;

export class UploadPreflightError extends Error {
  reason: UploadFailureReason;
  httpStatus?: number;

  constructor(
    reason: UploadFailureReason,
    message: string,
    options: { httpStatus?: number } = {},
  ) {
    super(message);
    this.name = 'UploadPreflightError';
    this.reason = reason;
    this.httpStatus = options.httpStatus;
    Object.setPrototypeOf(this, UploadPreflightError.prototype);
  }
}

interface PutObjectConfig {
  headers: Record<string, string>;
  timeout: number;
}

interface PutObjectResponse {
  headers: Record<string, string | undefined>;
}

type PutObject = (
  url: string,
  body: Blob,
  config: PutObjectConfig,
) => Promise<PutObjectResponse>;

interface RunS3UploadPreflightParams {
  target: UploadPreflightTarget;
  getOnline?: () => boolean;
  createPreflight?: (
    target: UploadPreflightTarget,
  ) => Promise<UploadPreflightResponse>;
  cleanupPreflight?: (
    target: UploadPreflightTarget,
    key: string,
  ) => Promise<void>;
  putObject?: PutObject;
}

interface RunUploadPreflightIfNeededParams {
  file?: File | null;
  target: UploadPreflightTarget;
  uploadPath: string;
  browserDirect?: boolean;
  force?: boolean;
}

interface PreflightSourceFileLike {
  sourceType: string;
  localFile?: unknown;
  desktopProxyPath?: string;
  renderServiceProxyJobId?: string;
  metadata?: {
    fileSize?: number;
  };
}

const defaultGetOnline = (): boolean =>
  typeof navigator === 'undefined' ? true : navigator.onLine !== false;

const defaultPutObject: PutObject = async (url, body, config) => {
  const response = await axios.put(url, body, config);

  return { headers: response.headers as Record<string, string | undefined> };
};

const defaultCreatePreflight = async (
  target: UploadPreflightTarget,
): Promise<UploadPreflightResponse> => {
  const { postUploadPreflightApi } =
    await import('@/shared/apis/asset-upload/post-upload-preflight');

  return postUploadPreflightApi({ target });
};

const defaultCleanupPreflight = async (
  target: UploadPreflightTarget,
  key: string,
): Promise<void> => {
  const { deleteUploadPreflightApi } =
    await import('@/shared/apis/asset-upload/post-upload-preflight');

  await deleteUploadPreflightApi({ target, key });
};

const getHttpStatus = (error: unknown): number | undefined => {
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    typeof (error as { response?: { status?: number } }).response?.status ===
      'number'
  ) {
    return (error as { response: { status: number } }).response.status;
  }

  return undefined;
};

const getErrorCode = (error: unknown): string | undefined => {
  if (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    typeof (error as { code?: string }).code === 'string'
  ) {
    return (error as { code: string }).code;
  }

  return undefined;
};

const classifyPreflightPutError = (
  error: unknown,
  attemptCount: number,
): UploadPreflightError => {
  const httpStatus = getHttpStatus(error);
  if (httpStatus === 403) {
    return new UploadPreflightError(
      'PREFLIGHT_FORBIDDEN',
      'Upload preflight forbidden',
      { httpStatus },
    );
  }

  const code = getErrorCode(error);
  if (code === 'ECONNABORTED') {
    return new UploadPreflightError(
      'PREFLIGHT_TIMEOUT',
      'Upload preflight timed out',
    );
  }

  if (code === 'ERR_NETWORK') {
    return new UploadPreflightError(
      attemptCount >= PREFLIGHT_MAX_ATTEMPTS
        ? 'PREFLIGHT_NETWORK_RESTRICTED'
        : 'PREFLIGHT_NETWORK_ERROR',
      'Upload preflight network error',
    );
  }

  return new UploadPreflightError(
    'PREFLIGHT_UNKNOWN',
    error instanceof Error ? error.message : 'Upload preflight failed',
    { httpStatus },
  );
};

export const shouldRunS3UploadPreflight = (
  files: PreflightSourceFileLike[],
): boolean =>
  files.some((file) => {
    if (file.sourceType !== 'LOCAL_FILE') return false;
    if (!file.localFile) return false;
    if (file.desktopProxyPath || file.renderServiceProxyJobId) return false;

    return (file.metadata?.fileSize ?? 0) >= UPLOAD_PREFLIGHT_MIN_BYTES;
  });

export const runS3UploadPreflight = async ({
  target,
  getOnline = defaultGetOnline,
  createPreflight = defaultCreatePreflight,
  cleanupPreflight = defaultCleanupPreflight,
  putObject = defaultPutObject,
}: RunS3UploadPreflightParams): Promise<'PASS'> => {
  if (!getOnline()) {
    throw new UploadPreflightError(
      'PREFLIGHT_OFFLINE',
      'Browser is offline before upload preflight',
    );
  }

  const preflight = await createPreflight(target);
  const body = new Blob([new Uint8Array(preflight.probeSizeBytes)], {
    type: 'application/octet-stream',
  });

  let lastError: UploadPreflightError | null = null;
  for (let attempt = 1; attempt <= PREFLIGHT_MAX_ATTEMPTS; attempt += 1) {
    try {
      const response = await putObject(preflight.preSignedUrl, body, {
        headers: preflight.requiredHeaders,
        timeout: PREFLIGHT_TIMEOUT_MS,
      });
      const eTag = response.headers.etag || response.headers.ETag;
      if (!eTag) {
        throw new UploadPreflightError(
          'PREFLIGHT_CORS_OR_ETAG',
          'Upload preflight succeeded but ETag header is not readable',
        );
      }

      await cleanupPreflight(target, preflight.key).catch(() => undefined);
      return 'PASS';
    } catch (error) {
      lastError =
        error instanceof UploadPreflightError
          ? error
          : classifyPreflightPutError(error, attempt);

      if (
        lastError.reason !== 'PREFLIGHT_NETWORK_ERROR' ||
        attempt === PREFLIGHT_MAX_ATTEMPTS
      ) {
        await cleanupPreflight(target, preflight.key).catch(() => undefined);
        throw lastError;
      }
    }
  }

  throw (
    lastError ??
    new UploadPreflightError('PREFLIGHT_UNKNOWN', 'Upload preflight failed')
  );
};

export const classifyUploadPreflightFailure = (
  error: unknown,
  fileName: string,
): UploadFailureClassification => {
  const reason =
    error instanceof UploadPreflightError ? error.reason : 'PREFLIGHT_UNKNOWN';

  return {
    reason,
    i18nKey: I18N_KEY_BY_REASON[reason],
    i18nValues: { name: fileName },
    raw: {
      httpStatus:
        error instanceof UploadPreflightError ? error.httpStatus : undefined,
      message: error instanceof Error ? error.message : undefined,
    },
  };
};

export const runUploadPreflightIfNeeded = async ({
  file,
  target,
  uploadPath,
  browserDirect = true,
  force,
}: RunUploadPreflightIfNeededParams): Promise<UploadPreflightDecision> => {
  const decision = getUploadPreflightDecision({
    file,
    target,
    uploadPath,
    browserDirect,
    force,
  });

  if (decision.shouldRun) {
    await runS3UploadPreflight({ target: decision.target });
  }

  return decision;
};
